import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Users, BookOpen, Trophy, Activity, Target, Award, Layers } from 'lucide-react';

const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444', '#14B8A6'];

export const AnalyticsPanel = ({ showLeaderboard = true }) => {
  const [data, setData] = useState({ bySubject: [], quizPerf: [], timeSeries: { events: [], quizzes: [] }, leaderboard: [], summary: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAnalytics(); }, []);

  const loadAnalytics = async () => {
    try {
      const [subjectRes, quizRes, tsRes, lbRes, summaryRes] = await Promise.all([
        api.get('/analytics/by-subject'),
        api.get('/analytics/quiz-performance'),
        api.get('/analytics/time-series'),
        showLeaderboard ? api.get('/analytics/leaderboard') : Promise.resolve({ data: [] }),
        api.get('/analytics/summary'),
      ]);
      setData({
        bySubject: subjectRes.data, quizPerf: quizRes.data, timeSeries: tsRes.data,
        leaderboard: lbRes.data, summary: summaryRes.data,
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-slate-500 text-sm">Loading analytics...</div>;

  const { bySubject, quizPerf, timeSeries, leaderboard, summary } = data;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Total Students', value: summary?.total_students || 0, icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Quiz Attempts', value: summary?.total_quiz_attempts || 0, icon: Target, color: 'text-cyan-600', bg: 'bg-cyan-50' },
          { label: 'Avg Score', value: `${Math.round(summary?.avg_quiz_score || 0)}%`, icon: Award, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'AR Views', value: summary?.events?.reduce((s, e) => s + e.count, 0) || 0, icon: Layers, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                  <s.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${s.color}`} />
                </div>
                <div>
                  <p className={`text-lg sm:text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] sm:text-xs text-slate-600">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="engagement" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto bg-slate-100">
          <TabsTrigger value="engagement" className="text-xs py-2"><Activity className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Engagement</span></TabsTrigger>
          <TabsTrigger value="subjects" className="text-xs py-2"><BookOpen className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Subjects</span></TabsTrigger>
          <TabsTrigger value="quizzes" className="text-xs py-2"><Target className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Quizzes</span></TabsTrigger>
          {showLeaderboard && <TabsTrigger value="leaderboard" className="text-xs py-2"><Trophy className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Leaders</span></TabsTrigger>}
        </TabsList>

        {/* Engagement Tab */}
        <TabsContent value="engagement" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="p-4 pb-2"><CardTitle className="text-sm text-indigo-900 flex items-center gap-2"><Activity className="w-4 h-4" /> Activity Timeline</CardTitle></CardHeader>
              <CardContent className="p-2 sm:p-4 pt-0">
                {timeSeries.events.length > 0 ? (
                  <div className="h-48 sm:h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timeSeries.events}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="views" stroke="#4F46E5" strokeWidth={2} name="Views" dot={false} />
                        <Line type="monotone" dataKey="scans" stroke="#06B6D4" strokeWidth={2} name="Scans" dot={false} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">No activity data yet</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="p-4 pb-2"><CardTitle className="text-sm text-indigo-900 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Quiz Score Trends</CardTitle></CardHeader>
              <CardContent className="p-2 sm:p-4 pt-0">
                {timeSeries.quizzes.length > 0 ? (
                  <div className="h-48 sm:h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timeSeries.quizzes}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="avg_score" stroke="#10B981" strokeWidth={2} name="Avg Score" dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="quizzes" stroke="#F59E0B" strokeWidth={2} name="Attempts" dot={{ r: 3 }} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">No quiz trends yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subjects Tab */}
        <TabsContent value="subjects" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="p-4 pb-2"><CardTitle className="text-sm text-indigo-900">Engagement by Subject</CardTitle></CardHeader>
              <CardContent className="p-2 sm:p-4 pt-0">
                {bySubject.length > 0 ? (
                  <div className="h-48 sm:h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bySubject} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="subject" tick={{ fontSize: 10 }} width={80} />
                        <Tooltip />
                        <Bar dataKey="total_views" fill="#4F46E5" radius={[0, 4, 4, 0]} name="Views" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">No subject data yet</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="p-4 pb-2"><CardTitle className="text-sm text-indigo-900">Students per Subject</CardTitle></CardHeader>
              <CardContent className="p-2 sm:p-4 pt-0">
                {bySubject.length > 0 ? (
                  <div className="h-48 sm:h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={bySubject.map(s => ({ name: s.subject || 'Unknown', value: s.unique_students }))}
                          cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`} labelLine={false}
                          dataKey="value" fontSize={10}>
                          {bySubject.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">No data</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Subject Detail Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {bySubject.map((s, i) => (
              <Card key={i} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <h4 className="font-bold text-sm text-slate-800">{s.subject || 'Unknown'}</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-lg font-bold text-indigo-600">{s.total_views}</p><p className="text-[10px] text-slate-500">Views</p></div>
                    <div><p className="text-lg font-bold text-cyan-600">{s.unique_students}</p><p className="text-[10px] text-slate-500">Students</p></div>
                    <div><p className="text-lg font-bold text-amber-600">{s.avg_duration || 0}s</p><p className="text-[10px] text-slate-500">Avg Time</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Quizzes Tab */}
        <TabsContent value="quizzes" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="p-4 pb-2"><CardTitle className="text-sm text-indigo-900">Quiz Performance by Concept</CardTitle></CardHeader>
            <CardContent className="p-2 sm:p-4 pt-0">
              {quizPerf.length > 0 ? (
                <div className="h-56 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={quizPerf}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="concept" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={60} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="avg_score" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Avg Score" />
                      <Bar dataKey="pass_rate" fill="#10B981" radius={[4, 4, 0, 0]} name="Pass Rate %" />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No quiz data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Quiz Detail Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quizPerf.map((q, i) => (
              <Card key={i} className="border-slate-200">
                <CardContent className="p-4">
                  <h4 className="font-bold text-sm text-indigo-900 mb-3">{q.concept || 'Unknown Concept'}</h4>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div><p className="text-base font-bold text-indigo-600">{q.attempts}</p><p className="text-[10px] text-slate-500">Attempts</p></div>
                    <div><p className="text-base font-bold text-cyan-600">{q.avg_score}%</p><p className="text-[10px] text-slate-500">Avg</p></div>
                    <div><p className="text-base font-bold text-emerald-600">{q.pass_rate}%</p><p className="text-[10px] text-slate-500">Pass Rate</p></div>
                    <div><p className="text-base font-bold text-amber-600">{q.best_score}%</p><p className="text-[10px] text-slate-500">Best</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Leaderboard Tab */}
        {showLeaderboard && (
          <TabsContent value="leaderboard" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="p-4 pb-2"><CardTitle className="text-sm text-indigo-900 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Student Leaderboard</CardTitle></CardHeader>
              <CardContent className="p-4 pt-0 space-y-2">
                {leaderboard.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">No quiz data yet</p>
                ) : leaderboard.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-amber-700' : 'bg-slate-300'}`}>
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{s.name || 'Student'}</p>
                        <p className="text-xs text-slate-500">{s.total_attempts} attempts</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-600 text-sm">{s.avg_score}%</p>
                      <p className="text-[10px] text-slate-500">Best: {s.best_score}%</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
